import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const TeamSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const teamMembers = [
    {
      name: 'Yogesh Patil',
      role: 'Managing Partner',
      description: '18 years of CRE Acquisition & Development across Texas Market',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/mxxpj177_WhatsApp%20Image%202026-01-17%20at%2012.50.46%20AM%20%281%29.jpeg',
      isAdvisor: false,
      education: ['M.S. Industrial Engineering & Management']
    },
    {
      name: 'Samara Shaw',
      role: 'CIO',
      description: '12 years of Commercial RE Value add experience',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/2vy31jtr_d162efa6-4185-45d6-ae10-ab704214fa3e.png',
      isAdvisor: false,
      education: ['MBA Finance']
    },
    {
      name: 'Deelip Mhaske',
      role: 'Commissioner & President',
      description: 'Commissioner, New Jersey India Commission. President, Foundation for Human Horizon.',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/61g9n1xa_1763095137035.jpg',
      isAdvisor: false,
      education: ['Harvard Business School', 'MPhil IIT Bombay', 'MBA Rutgers Business School']
    },
    {
      name: 'Pravin Patil',
      role: 'VC | Deep Tech & AI',
      description: 'Strategic advisor bringing venture capital and technology expertise',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/nu3jq0hl_1664893226150.jpg',
      isAdvisor: true,
      education: ['MBA', 'B.Tech Computer Science']
    }
  ];

  const medicalAdvisor = {
    name: 'Dr. Bhupesh Patil',
    role: 'Medical Advisor',
    description: 'Doctor, Diabetic Specialist, and Cardiologist',
    image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/0nki0e9w_Dr%20Bhupesh%20Patil.jpg',
    isAdvisor: true
  };

  return (
    <section id="team" className="relative gradient-section-alt py-16 lg:py-24 overflow-hidden">
      {/* Decorative Elements */}
      <motion.div 
        className="absolute top-40 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[120px]"
        animate={{ 
          x: [0, 40, 0],
          y: [0, -30, 0]
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-20 right-0 w-80 h-80 bg-[#1a2940]/50 rounded-full blur-[100px]"
        animate={{ 
          x: [0, -30, 0],
          y: [0, 40, 0]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-full mb-5 shadow-lg shadow-[#D4AF37]/20"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            <span className="text-label text-[#0A1628]">Leadership</span>
          </motion.div>
          <motion.h2 
            className="heading-h2 mb-4"
            variants={itemVariants}
          >
            Our Team & <span className="gradient-text">Advisors</span>
          </motion.h2>
          <motion.p 
            className="text-body mb-6"
            variants={itemVariants}
          >
            Experienced professionals with decades of combined expertise in real estate, finance, 
            technology, and strategic advisory.
          </motion.p>
          <motion.div variants={itemVariants}>
            <Link to="/team">
              <Button 
                className="group bg-transparent border-2 border-[#D4AF37]/40 hover:border-[#D4AF37] text-white font-semibold px-6 py-2.5 h-11 rounded-full text-sm transition-all duration-300 inline-flex items-center gap-2"
                data-testid="meet-full-team-btn"
              >
                Meet Our Full Team
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>

        {/* ASAP Capital Team & Partners */}
        <div className="mb-16">
          <motion.h3 
            className="text-white text-xl font-bold text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            ASAP Capital Team & Partners
          </motion.h3>
          
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={containerVariants}
          >
            {teamMembers.map((member, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
              >
                <Card className="group card-modern rounded-3xl p-6 text-center h-full">
                  <motion.div 
                    className="relative w-32 h-32 mx-auto mb-5"
                    whileHover={{ scale: 1.05 }}
                  >
                    <motion.div 
                      className="absolute inset-0 rounded-full border-2 border-[#D4AF37]/50"
                      animate={{ 
                        boxShadow: ['0 0 15px rgba(212,175,55,0.2)', '0 0 25px rgba(212,175,55,0.4)', '0 0 15px rgba(212,175,55,0.2)']
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full rounded-full object-cover object-top"
                    />
                  </motion.div>
                  
                  {member.isAdvisor && (
                    <motion.div 
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 rounded-full mb-3"
                      whileHover={{ scale: 1.05 }}
                    >
                      <span className="text-[#D4AF37] text-xs font-semibold">Advisor</span>
                    </motion.div>
                  )}
                  
                  <h4 className="text-white text-lg font-bold mb-1">{member.name}</h4>
                  <p className="gradient-text text-sm font-medium mb-3">{member.role}</p>
                  <p className="text-white/50 text-sm leading-relaxed mb-4">
                    {member.description}
                  </p>
                  
                  {member.education && (
                    <div className="pt-3 border-t border-white/10">
                      <p className="text-white/40 text-xs mb-2">Education</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {member.education.map((edu, eduIndex) => (
                          <motion.span 
                            key={eduIndex}
                            className="bg-[#1a2940] border border-white/10 rounded-full px-3 py-1 text-white/70 text-xs"
                            whileHover={{ scale: 1.05, borderColor: 'rgba(212, 175, 55, 0.3)' }}
                          >
                            {edu}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Medical Advisory */}
        <div>
          <motion.h3 
            className="text-white text-xl font-bold text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Medical Advisory
          </motion.h3>
          
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="card-modern rounded-3xl p-8 text-center max-w-sm">
              <motion.div 
                className="relative w-32 h-32 mx-auto mb-5"
                whileHover={{ scale: 1.05 }}
              >
                <motion.div 
                  className="absolute inset-0 rounded-full border-2 border-[#D4AF37]/50"
                  animate={{ 
                    boxShadow: ['0 0 15px rgba(212,175,55,0.2)', '0 0 25px rgba(212,175,55,0.4)', '0 0 15px rgba(212,175,55,0.2)']
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <img 
                  src={medicalAdvisor.image}
                  alt={medicalAdvisor.name}
                  className="w-full h-full rounded-full object-cover object-top"
                />
              </motion.div>
              
              <motion.div 
                className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 rounded-full mb-3"
                whileHover={{ scale: 1.05 }}
              >
                <span className="text-[#D4AF37] text-xs font-semibold">Advisor</span>
              </motion.div>
              
              <h4 className="text-white text-xl font-bold mb-1">{medicalAdvisor.name}</h4>
              <p className="gradient-text text-sm font-medium mb-3">{medicalAdvisor.role}</p>
              <p className="text-white/50 text-sm leading-relaxed">
                {medicalAdvisor.description}
              </p>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
